import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Trash2, Receipt, ChevronLeft, ChevronRight, ChevronDown,
  AlertTriangle, CheckCircle, XCircle, Wallet, CreditCard,
  TrendingDown, PieChart, Pencil, Save, Link2, Unlink, CalendarCheck, X, ArrowRight, Sparkles, RotateCcw, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format, subMonths, addMonths, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart as RechartPie, Pie, Cell, LineChart, Line, Legend
} from 'recharts';
import axios from 'axios';
import SankeyChart from './finances/SankeyChart';
import RecapContent from './finances/RecapContent';
import { InlineAdd, ChargeAdd, EnvForm, NewEnvelopeForm } from './finances/forms';
import {
  FAMILY_META, DEST_COLORS, ENV_TYPE_TO_CAT, CAT_TO_TYPE,
  DEFAULT_CATS, CHARGE_CATS, PIE_COLORS, EMPTY_BUDGET,
} from './finances/constants';
import { uid, fmt } from './finances/utils';

// ═══════════════ MAIN PAGE ═══════════════

const FinancesPage = () => {
  const { token, user } = useAuth();
  const userId = user?.id || 'anon';
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const hAuth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const storeRef = useRef({});
  const saveTimerRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);

  const persistStore = useCallback(() => {
    if (!hydrated) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      axios.put(`${API}/store/finances`, storeRef.current, hAuth).catch(e => console.warn('Autosave finances failed', e));
    }, 500);
  }, [API, hAuth, hydrated]);

  // Backend-backed key/value adapter (matches the previous `ls` interface so the rest of the code works unchanged).
  // ALL keys (including streak and goal_overrides) are stored in the finances blob in MongoDB for cross-device consistency.
  const ls = useMemo(() => ({
    get: (k, fb) => {
      const s = storeRef.current || {};
      if (k.startsWith('m_')) return (s.monthly && s.monthly[k.slice(2)]) ?? fb;
      if (k.startsWith('ep_')) return (s.envelopePayments && s.envelopePayments[k.slice(3)]) ?? fb;
      if (k.startsWith('recap_dismissed_')) return (s.recapDismissed && s.recapDismissed[k.slice('recap_dismissed_'.length)]) ?? fb;
      return s[k] ?? fb;
    },
    set: (k, v) => {
      const s = storeRef.current || {};
      if (k.startsWith('m_')) {
        s.monthly = { ...(s.monthly || {}), [k.slice(2)]: v };
      } else if (k.startsWith('ep_')) {
        s.envelopePayments = { ...(s.envelopePayments || {}), [k.slice(3)]: v };
      } else if (k.startsWith('recap_dismissed_')) {
        s.recapDismissed = { ...(s.recapDismissed || {}), [k.slice('recap_dismissed_'.length)]: v };
      } else {
        s[k] = v;
      }
      storeRef.current = s;
      persistStore();
    },
  }), [persistStore]);

  const [tab, setTab] = useState('budget');
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const handleResetFinances = async () => {
    try {
      await axios.delete(`${API}/reset/finances`, hAuth);
    } catch {}
    // Clear in-memory store
    storeRef.current = {};
    // Clear ONLY finance-related localStorage keys (do not touch ptr_*, goal_overrides, etc.)
    const prefix = `capos_${userId}_`;
    const FINANCE_KEYS = new Set(['budget', 'env_links', 'custom_subs', 'custom_cats', 'recurring', 'streak']);
    const FINANCE_PREFIXES = ['m_', 'ep_', 'recap_dismissed_'];
    Object.keys(localStorage).forEach(k => {
      if (!k.startsWith(prefix)) return;
      const suffix = k.slice(prefix.length);
      if (FINANCE_KEYS.has(suffix) || FINANCE_PREFIXES.some(p => suffix.startsWith(p))) {
        localStorage.removeItem(k);
      }
    });
    setShowResetConfirm(false);
    toast.success('Données réinitialisées avec succès');
    window.location.reload();
  };

  // Budget state
  const [budget, setBudget] = useState(() => ls.get('budget', EMPTY_BUDGET));
  const [step, setStep] = useState(1);
  const [editing, setEditing] = useState(() => !ls.get('budget', null));

  // Month state
  const [mData, setMData] = useState({ revenues: [], expenses: [] });
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ amount: '', date: '', description: '', family: '', category: '', subcategory: '', isRecurring: false, recurringDay: '1' });
  const [selCat, setSelCat] = useState(null);

  // Savings
  const [envPay, setEnvPay] = useState({});
  const [envLinks, setEnvLinks] = useState(() => ls.get('env_links', {}));
  const [objectives, setObjectives] = useState([]);
  const [profile, setProfile] = useState(null);
  const [customSubs, setCustomSubs] = useState(() => ls.get('custom_subs', {}));
  const [customCats, setCustomCats] = useState(() => ls.get('custom_cats', []));

  // Monthly recap state
  const [recapOpen, setRecapOpen] = useState(false);
  const [expandedSavMonth, setExpandedSavMonth] = useState(null);
  const [expandedBlock, setExpandedBlock] = useState(null);

  // Recurring expense edit modals
  const [recAmountModal, setRecAmountModal] = useState({ open: false, exp: null, oldAmount: 0, newAmount: 0 });
  const [recOptionsModal, setRecOptionsModal] = useState({ open: false, exp: null, endDate: '' });

  // Patrimoine savings accounts (used to dynamically populate the "Épargne" subcategory dropdown
  // and auto-credit the matching account's balance when validating the expense).
  const [patrimoineSavings, setPatrimoineSavings] = useState([]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/store/patrimoine`, hAuth)
      .then(r => setPatrimoineSavings(Array.isArray(r.data?.savings) ? r.data.savings : []))
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hydrate from backend on mount ──
  // Resets `hydrated` on every remount so navigating away & back triggers a fresh load,
  // and the local state (budget/mData/envLinks/...) is re-synced from the freshly fetched store.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setHydrated(false);
    (async () => {
      try {
        const r = await axios.get(`${API}/store/finances`, hAuth);
        if (cancelled) return;
        storeRef.current = r.data || {};
        const s = storeRef.current;
        if (s.budget) { setBudget(s.budget); setEditing(false); }
        else { setBudget(EMPTY_BUDGET); setEditing(true); }
        setEnvLinks(s.env_links || {});
        setCustomSubs(s.custom_subs || {});
        setCustomCats(s.custom_cats || []);
      } catch (e) {
        console.warn('Failed to load /store/finances', e);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──
  const loadMonth = useCallback((m) => {
    const b = ls.get('budget', EMPTY_BUDGET);
    const existing = ls.get(`m_${m}`, null);
    let d = existing ? { ...existing } : { revenues: [], expenses: [], extra_incomes: [] };

    // ─── Legacy migration (robust): pre-fix versions stored manual "revenus complémentaires"
    //     inline in `d.revenues` (with `isBudget: false`, `isBudget: undefined`, or even no flag).
    //     The simple `!Array.isArray(extra_incomes)` check missed the case where a previous load
    //     had already set `extra_incomes = []` — orphan items in `d.revenues` then got wiped on
    //     rebuild. We now ALWAYS scan `d.revenues` for items that don't match any budget revenue
    //     and merge them into `extra_incomes` (deduplicated by name+amount). Idempotent.
    {
      const budgetNames = new Set(
        (b.revenues || [])
          .map(r => (r?.name || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const currentExtras = Array.isArray(d.extra_incomes) ? d.extra_incomes : [];
      const extraKeys = new Set(
        currentExtras
          .filter(r => r && r.name)
          .map(r => `${r.name.trim().toLowerCase()}|${parseFloat(r.amount) || 0}`)
      );
      const orphaned = (d.revenues || [])
        .filter(r => {
          if (!r || !r.name) return false;
          const nameLc = r.name.trim().toLowerCase();
          if (!nameLc) return false;
          // Explicitly tagged manual extra
          if (r.isBudget === false) return true;
          // Untagged but absent from budget template → legacy manual extra
          if (r.isBudget === undefined && !budgetNames.has(nameLc)) return true;
          return false;
        })
        .map(r => ({
          id: r.id || uid(),
          name: r.name,
          amount: parseFloat(r.amount) || 0,
        }))
        .filter(r => {
          const k = `${r.name.trim().toLowerCase()}|${r.amount}`;
          if (extraKeys.has(k)) return false;
          extraKeys.add(k); // dedupe within the orphans themselves
          return true;
        });
      d.extra_incomes = [...currentExtras, ...orphaned];
    }

    // Always sync fixed charges from budget (only if budget has data)
    if (b.fixedCharges && b.fixedCharges.length > 0) {
      d.expenses = (d.expenses || []).filter(e => !e.isFixed);
      const overrides = d.fixed_overrides || {};
      b.fixedCharges.forEach(c => {
        const ovr = overrides[c.name] || {};
        if (ovr.deleted) return; // user removed this charge for this month
        if (c.startDate && m < String(c.startDate).slice(0, 7)) return; // before start month → not yet active
        if (c.endDate && m > String(c.endDate).slice(0, 7)) return; // beyond end month → skip
        const amount = (ovr.amount != null) ? parseFloat(ovr.amount) : parseFloat(c.amount);
        d.expenses.push({ id: uid(), amount, date: `${m}-05`, description: c.name, family: 'obligatoire', category: c.category || 'Charges fixes', subcategory: null, isFixed: true, isDebtLinked: c.isDebtLinked || false, debtId: c.debtId || null });
      });
    }

    // Inject recurring for new months only
    if (!existing) {
      const rec = ls.get('recurring', []);
      rec.forEach(r => {
        if (r.startDate && m < String(r.startDate).slice(0, 7)) return; // before start month → not yet active
        if (r.endDate && m > String(r.endDate).slice(0, 7)) return; // beyond end month → skip
        if (!d.expenses.some(e => e.description === r.description && Math.abs(e.amount - r.amount) < 0.01))
          d.expenses.push({ id: uid(), amount: parseFloat(r.amount), date: `${m}-${String(r.day || 1).padStart(2, '0')}`, description: r.description, family: r.family, category: r.category, subcategory: r.subcategory, isRecurring: true });
      });
    }

    // ─── Rebuild revenues for display: budget revenues (always fresh from global) + per-month extras.
    const budgetRevs = (b.revenues || []).map(r => ({ ...r, id: uid(), isBudget: true }));
    const extraRevs = (d.extra_incomes || []).map(r => ({ ...r, isBudget: false }));
    d.revenues = [...budgetRevs, ...extraRevs];

    // Only save to localStorage if we have meaningful data (prevents overwriting with empty)
    if (d.revenues.length > 0 || d.expenses.length > 0 || (d.extra_incomes && d.extra_incomes.length > 0) || existing) {
      ls.set(`m_${m}`, d);
    }
    setMData(d);
    setEnvPay(ls.get(`ep_${m}`, {}));
  }, [ls]);
  // ── Propagate budget changes to current + future months ──
  const propagateBudgetToFutureMonths = useCallback((b) => {
    const now = format(new Date(), 'yyyy-MM');

    // Collect all month keys from the in-memory finances blob (stored in MongoDB via storeRef).
    const monthly = (storeRef.current && storeRef.current.monthly) || {};
    const monthKeys = Object.keys(monthly).filter(m => m >= now);

    monthKeys.forEach(m => {
      const d = ls.get(`m_${m}`, null);
      if (!d) return;

      // Update budget-sourced revenues (isBudget: true). Manual extras live in `extra_incomes` and stay untouched.
      const extras = (d.extra_incomes || []).map(r => ({ ...r, isBudget: false }));
      const budgetRevenues = b.revenues.map(r => ({ ...r, id: uid(), isBudget: true }));
      d.revenues = [...budgetRevenues, ...extras];

      // Update fixed charges (isFixed: true), keep all variable expenses
      const nonFixedExpenses = (d.expenses || []).filter(e => !e.isFixed);
      const fixedExpenses = (b.fixedCharges || [])
        .filter(c => (!c.startDate || m >= String(c.startDate).slice(0, 7)) && (!c.endDate || m <= String(c.endDate).slice(0, 7)))
        .map(c => ({
          id: uid(), amount: parseFloat(c.amount), date: `${m}-05`,
          description: c.name, family: 'obligatoire',
          category: c.category || 'Charges fixes', subcategory: null,
          isFixed: true, isDebtLinked: c.isDebtLinked || false, debtId: c.debtId || null
        }));
      d.expenses = [...fixedExpenses, ...nonFixedExpenses];

      ls.set(`m_${m}`, d);
    });
  }, [ls]);

  const save = useCallback((b) => {
    setBudget(b);
    ls.set('budget', b);
    propagateBudgetToFutureMonths(b);
    loadMonth(month);
  }, [ls, month, loadMonth, propagateBudgetToFutureMonths]);
  const saveM = useCallback((m, d) => { ls.set(`m_${m}`, d); setMData(d); }, [ls]);
  const saveEP = useCallback((m, p) => { ls.set(`ep_${m}`, p); setEnvPay(p); }, [ls]);

  useEffect(() => { loadMonth(month); }, [month, loadMonth]);

  // ── Budget snapshot: freeze the budget AND the recurring list into the OUTGOING month
  // when the user navigates away. Only writes a snapshot when the outgoing month has none
  // yet — once frozen, a month's planned values are immutable (even if the global budget
  // changes later via "Modifier à partir de ce mois"). The Analysis tab reads
  // mData.budget_snapshot / mData.recurring_snapshot first.
  const prevMonthRef = useRef(month);
  useEffect(() => {
    if (!hydrated) { prevMonthRef.current = month; return; }
    const outgoing = prevMonthRef.current;
    prevMonthRef.current = month;
    if (!outgoing || outgoing === month) return;
    const monthly = (storeRef.current && storeRef.current.monthly) || {};
    const d = monthly[outgoing];
    if (!d) return;
    if (d.budget_snapshot && d.recurring_snapshot) return;
    const next = { ...d };
    if (!next.budget_snapshot) next.budget_snapshot = budget;
    if (!next.recurring_snapshot) next.recurring_snapshot = ls.get('recurring', []);
    storeRef.current.monthly = { ...monthly, [outgoing]: next };
    persistStore();
  }, [month, hydrated, budget, ls, persistStore]);

  // Re-read budget from localStorage when userId changes (fixes anon → real user transition)
  useEffect(() => {
    if (userId === 'anon') return;
    const b = ls.get('budget', EMPTY_BUDGET);
    setBudget(b);
    setEditing(!b.revenues || !b.revenues.length);
    loadMonth(month);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setProfile(r.data)).catch(() => {});
    axios.get(`${API}/objectives`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setObjectives(r.data)).catch(() => {});
  }, [token]);

  // ── Monthly recap trigger ──
  const prevMonthKey = useMemo(() => format(subMonths(new Date(), 1), 'yyyy-MM'), []);
  const prevMonthLabel = useMemo(() => format(subMonths(new Date(), 1), 'MMMM yyyy', { locale: fr }), []);

  const recap = useMemo(() => {
    const bud = ls.get('budget', EMPTY_BUDGET);
    const pData = ls.get(`m_${prevMonthKey}`, null);
    if (!pData || (!pData.revenues.length && !pData.expenses.length)) return null;

    const actualRev = pData.revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const actualExp = pData.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const plannedRev = bud.revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const plannedExp = bud.fixedCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
      + bud.variableCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
      + bud.envelopes.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const saved = actualRev - actualExp;
    const savingsRate = actualRev > 0 ? (saved / actualRev) * 100 : 0;

    // 50/30/20
    const fam = { obligatoire: 0, non_essentiel: 0, epargne_invest: 0 };
    pData.expenses.forEach(e => { if (fam[e.family] !== undefined) fam[e.family] += e.amount; });
    const pctBesoins = actualRev > 0 ? (fam.obligatoire / actualRev) * 100 : 0;
    const pctEnvies = actualRev > 0 ? (fam.non_essentiel / actualRev) * 100 : 0;
    const pctEpargne = actualRev > 0 ? (fam.epargne_invest / actualRev) * 100 : 0;
    const ruleOk = pctBesoins <= 55 && pctEnvies <= 35 && pctEpargne >= 15;

    // Over-budget items
    const overs = [];
    bud.fixedCharges.forEach(c => {
      const real = pData.expenses.filter(e => e.description === c.name && e.isFixed).reduce((s, e) => s + e.amount, 0);
      const planned = parseFloat(c.amount) || 0;
      if (real > planned * 1.05 && planned > 0) overs.push({ name: c.name, over: real - planned });
    });
    bud.variableCharges.forEach(c => {
      const real = pData.expenses.filter(e => e.description === c.name && e.isVariable).reduce((s, e) => s + e.amount, 0);
      const planned = parseFloat(c.amount) || 0;
      if (real > planned * 1.05 && planned > 0) overs.push({ name: c.name, over: real - planned });
    });
    overs.sort((a, b) => b.over - a.over);

    // Determine level & message
    let level, message;
    const nextM = format(new Date(), 'MMMM', { locale: fr });
    if (saved >= 0 && overs.length === 0 && savingsRate >= 15) {
      level = 'green';
      message = `Excellent mois ! Tu as respecté ton budget et épargné ${fmt(saved)}`;
    } else if (saved >= 0 && overs.length <= 2) {
      level = 'yellow';
      message = overs.length > 0
        ? `Mois correct, mais tes dépenses ${overs[0].name} ont dépassé de ${fmt(overs[0].over)}`
        : `Mois correct ! Tu peux encore optimiser ton taux d'épargne (${savingsRate.toFixed(0)}%)`;
    } else {
      level = 'red';
      message = `Budget dépassé ce mois. Voici les postes à surveiller en ${nextM}`;
    }

    return {
      actualRev, actualExp, plannedRev, plannedExp, saved, savingsRate,
      fam, pctBesoins, pctEnvies, pctEpargne, ruleOk,
      overs, level, message
    };
  }, [prevMonthKey]);

  // Auto-show recap on first visit of the new month
  useEffect(() => {
    if (!recap) return;
    const key = `recap_dismissed_${prevMonthKey}`;
    if (!ls.get(key, false)) {
      const timer = setTimeout(() => setRecapOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [recap, prevMonthKey]);

  const dismissRecap = () => {
    setRecapOpen(false);
    ls.set(`recap_dismissed_${prevMonthKey}`, true);
  };

  // ── Derived ──
  const totalRevM = mData.revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const totalExpM = mData.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const reste = totalRevM - totalExpM;

  const catTotals = useMemo(() => {
    const m = {};
    mData.expenses.forEach(e => { m[e.category] = (m[e.category] || 0) + e.amount; });
    return Object.entries(m).sort(([, a], [, b]) => b - a);
  }, [mData.expenses]);

  const famTotals = useMemo(() => {
    const r = { obligatoire: 0, non_essentiel: 0, epargne_invest: 0 };
    mData.expenses.forEach(e => { if (r[e.family] !== undefined) r[e.family] += e.amount; });
    return r;
  }, [mData.expenses]);

  const getCats = (fam) => {
    const base = { ...DEFAULT_CATS[fam] };
    // Unify: add all CHARGE_CATS
    CHARGE_CATS.forEach(cat => { if (!base[cat]) base[cat] = []; });
    // Add categories used in current budget charges
    [...budget.fixedCharges, ...budget.variableCharges].forEach(c => {
      if (c.category && !base[c.category]) base[c.category] = [];
    });
    // Add custom categories
    customCats.forEach(cat => { if (!base[cat]) base[cat] = []; });
    // Add custom subcategories
    const c = customSubs[fam] || {};
    Object.entries(c).forEach(([cat, subs]) => { base[cat] = [...(base[cat] || []), ...subs]; });
    // ── Dynamic override: replace the "Épargne" subcategory list with the user's real
    //    savings accounts from Patrimoine. Falls back to the static list when empty.
    if (fam === 'epargne_invest') {
      const accountNames = (patrimoineSavings || []).map(s => s.name).filter(Boolean);
      if (accountNames.length > 0) base['Épargne'] = accountNames;
    }
    return base;
  };

  // History
  const history = useMemo(() => {
    const res = [];
    for (let i = 5; i >= 0; i--) {
      const m = format(subMonths(new Date(), i), 'yyyy-MM');
      const d = ls.get(`m_${m}`, { revenues: [], expenses: [] });
      const total = d.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      const fam = { obligatoire: 0, non_essentiel: 0, epargne_invest: 0 };
      d.expenses.forEach(e => { if (fam[e.family] !== undefined) fam[e.family] += e.amount; });
      const rev = d.revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      res.push({ month: m, label: format(parse(m, 'yyyy-MM', new Date()), 'MMM yy', { locale: fr }), total, ...fam, revenues: rev });
    }
    return res;
  }, [month, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  const catHist = useMemo(() => {
    if (!selCat) return [];
    return Array.from({ length: 6 }, (_, i) => {
      const m = format(subMonths(new Date(), 5 - i), 'yyyy-MM');
      const d = ls.get(`m_${m}`, { expenses: [] });
      return { label: format(parse(m, 'yyyy-MM', new Date()), 'MMM yy', { locale: fr }), amount: d.expenses.filter(e => e.category === selCat).reduce((s, e) => s + e.amount, 0) };
    });
  }, [selCat, hydrated]);

  // Sankey comparison for analysis — groups by CATEGORY.
  // "Prévu" = the planned amounts of every recurring item active for `month` (fixed charges
  // from the budget + items from the recurring list + envelopes/variable charges), grouped
  // by category. For PAST months we prefer the frozen snapshots so historical Analysis
  // doesn't change when the user edits the current budget/recurring template.
  const comparison = useMemo(() => {
    const refBudget = mData.budget_snapshot || budget;
    const refRecurring = mData.recurring_snapshot || ls.get('recurring', []);
    const inRange = (item) => {
      if (item.startDate && month < String(item.startDate).slice(0, 7)) return false;
      if (item.endDate && month > String(item.endDate).slice(0, 7)) return false;
      return true;
    };
    const isDeleted = (name) => Boolean((mData.fixed_overrides || {})[name]?.deleted);
    const cats = {};
    const addPlanned = (cat, amount, isExpense) => {
      const c = cat || 'Autres';
      if (!cats[c]) cats[c] = { planned: 0, real: 0, isExpense };
      cats[c].planned += parseFloat(amount) || 0;
    };
    // Fixed charges (red) → planned, respecting start/end dates and per-month deletion.
    (refBudget.fixedCharges || []).forEach(c => {
      if (!inRange(c) || isDeleted(c.name)) return;
      const ovr = (mData.fixed_overrides || {})[c.name] || {};
      const amount = ovr.amount != null ? parseFloat(ovr.amount) : parseFloat(c.amount);
      addPlanned(c.category || 'Charges fixes', amount, true);
    });
    // Recurring list items (red OR yellow depending on family) → planned.
    (refRecurring || []).forEach(r => {
      if (!inRange(r)) return;
      addPlanned(r.category, r.amount, r.family !== 'epargne_invest');
    });
    // Variable charges → planned per category (no time scope).
    (refBudget.variableCharges || []).forEach(c => addPlanned(c.category, c.amount, true));
    // Envelopes → planned per type-mapped category (Épargne, Investissement, Provisions).
    (refBudget.envelopes || []).forEach(e => addPlanned(ENV_TYPE_TO_CAT[e.type], e.amount, false));
    // Mon Mois expenses → real per category
    mData.expenses.forEach(e => {
      const cat = e.category || 'Autres';
      if (!cats[cat]) cats[cat] = { planned: 0, real: 0, isExpense: e.family !== 'epargne_invest' };
      cats[cat].real += parseFloat(e.amount) || 0;
    });
    // For savings, also check envPay (manual entries in Mon Épargne)
    Object.entries(ENV_TYPE_TO_CAT).forEach(([type, cat]) => {
      const epTotal = (refBudget.envelopes || [])
        .filter(e => e.type === type)
        .reduce((s, e) => s + (parseFloat(envPay[e.name]) || 0), 0);
      if (cats[cat] && epTotal > cats[cat].real) cats[cat].real = epTotal;
    });
    return Object.entries(cats)
      .map(([name, data]) => ({ name, planned: data.planned, real: data.real, diff: data.real - data.planned, isExpense: data.isExpense }))
      .sort((a, b) => b.planned - a.planned);
  }, [budget, mData.expenses, mData.budget_snapshot, mData.recurring_snapshot, mData.fixed_overrides, envPay, month, ls]);

  const envCum = useMemo(() => {
    const c = {};
    budget.envelopes.forEach(e => { c[e.name] = 0; });
    for (let i = 11; i >= 0; i--) {
      const m = format(subMonths(new Date(), i), 'yyyy-MM');
      const ep = ls.get(`ep_${m}`, {});
      Object.entries(ep).forEach(([n, a]) => { c[n] = (c[n] || 0) + (parseFloat(a) || 0); });
    }
    return c;
  }, [budget.envelopes, month, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Savings history (12 months, cumulated by type) ──
  const savingsHist = useMemo(() => {
    const data = [];
    let cumE = 0, cumI = 0, cumP = 0;
    for (let i = 11; i >= 0; i--) {
      const m = format(subMonths(new Date(), i), 'yyyy-MM');
      const ep = ls.get(`ep_${m}`, {});
      let me = 0, mi = 0, mp = 0;
      budget.envelopes.forEach(env => {
        const v = parseFloat(ep[env.name]) || 0;
        if (env.type === 'epargne') me += v;
        else if (env.type === 'investissement') mi += v;
        else if (env.type === 'provision') mp += v;
      });
      cumE += me; cumI += mi; cumP += mp;
      data.push({
        month: m,
        label: format(parse(m, 'yyyy-MM', new Date()), 'MMM yy', { locale: fr }),
        fullLabel: format(parse(m, 'yyyy-MM', new Date()), 'MMMM yyyy', { locale: fr }),
        epargne: me, invest: mi, provision: mp, total: me + mi + mp,
        cumEpargne: cumE, cumInvest: cumI, cumProvision: cumP, cumTotal: cumE + cumI + cumP,
        detail: budget.envelopes.map(env => ({ name: env.name, type: env.type, amount: parseFloat(ep[env.name]) || 0 })).filter(d => d.amount > 0)
      });
    }
    return data;
  }, [budget.envelopes, month, hydrated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Savings blocks from Mon Mois expenses (drives Mon Épargne tab) ──
  const savingsBlocks = useMemo(() => {
    const blocks = {
      investissement: { label: 'Investissement', color: '#4f8eff', total: 0, subs: {} },
      epargne: { label: 'Épargne', color: '#00e5a0', total: 0, subs: {} },
      provision: { label: 'Provisions', color: '#7c3aed', total: 0, subs: {} }
    };
    // Current month totals
    mData.expenses.filter(e => e.family === 'epargne_invest').forEach(e => {
      const type = CAT_TO_TYPE[e.category];
      if (!type || !blocks[type]) return;
      const amt = parseFloat(e.amount) || 0;
      const sub = e.subcategory || e.description || 'Autre';
      blocks[type].total += amt;
      if (!blocks[type].subs[sub]) blocks[type].subs[sub] = { month: 0, cum: 0 };
      blocks[type].subs[sub].month += amt;
    });
    // Cumulative across 12 months
    for (let i = 11; i >= 0; i--) {
      const m = format(subMonths(new Date(), i), 'yyyy-MM');
      const md = ls.get(`m_${m}`, { expenses: [] });
      (md.expenses || []).filter(e => e.family === 'epargne_invest').forEach(e => {
        const type = CAT_TO_TYPE[e.category];
        if (!type || !blocks[type]) return;
        const sub = e.subcategory || e.description || 'Autre';
        if (!blocks[type].subs[sub]) blocks[type].subs[sub] = { month: 0, cum: 0 };
        blocks[type].subs[sub].cum += parseFloat(e.amount) || 0;
      });
    }
    return blocks;
  }, [mData.expenses, month, ls, hydrated]);

  // ── Auto-sync envelope payments from Mon Mois expenses ──
  useEffect(() => {
    if (!budget.envelopes.length) return;
    const newPay = { ...envPay };
    let changed = false;
    const catToType = { 'Épargne': 'epargne', 'Investissement': 'investissement', 'Provisions': 'provision' };

    // Reset totals for auto-sync
    const envTotals = {};
    budget.envelopes.forEach(env => { envTotals[env.name] = 0; });

    mData.expenses
      .filter(e => e.family === 'epargne_invest')
      .forEach(e => {
        const amt = parseFloat(e.amount) || 0;
        if (amt <= 0) return;
        // 1. Match by description → envelope name (exact)
        const byDesc = budget.envelopes.find(env => env.name === e.description);
        if (byDesc) { envTotals[byDesc.name] += amt; return; }
        // 2. Match by subcategory → envelope name (exact)
        if (e.subcategory) {
          const bySub = budget.envelopes.find(env => env.name === e.subcategory);
          if (bySub) { envTotals[bySub.name] += amt; return; }
        }
        // 3. Match by category → envelope type, distribute proportionally to target amounts
        const targetType = catToType[e.category];
        if (targetType) {
          const typeEnvs = budget.envelopes.filter(env => env.type === targetType);
          if (typeEnvs.length === 1) {
            envTotals[typeEnvs[0].name] += amt;
          } else if (typeEnvs.length > 1) {
            const totalTarget = typeEnvs.reduce((s, ev) => s + (parseFloat(ev.amount) || 0), 0);
            typeEnvs.forEach(ev => {
              const ratio = totalTarget > 0 ? (parseFloat(ev.amount) || 0) / totalTarget : 1 / typeEnvs.length;
              envTotals[ev.name] += amt * ratio;
            });
          }
        }
      });

    budget.envelopes.forEach(env => {
      const total = Math.round(envTotals[env.name] * 100) / 100;
      if (total > 0 && Math.abs((parseFloat(newPay[env.name]) || 0) - total) > 0.01) {
        newPay[env.name] = total;
        changed = true;
      }
    });
    if (changed) saveEP(month, newPay);
  }, [mData.expenses, budget.envelopes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Streak computation (consecutive months under budget) ──
  const streak = useMemo(() => {
    const bud = ls.get('budget', EMPTY_BUDGET);
    const plannedTotal = bud.fixedCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
      + bud.variableCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0)
      + bud.envelopes.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    if (plannedTotal <= 0) return 0;
    let count = 0;
    for (let i = 1; i <= 24; i++) {
      const m = format(subMonths(new Date(), i), 'yyyy-MM');
      const d = ls.get(`m_${m}`, null);
      if (!d || !d.expenses.length) break;
      const actual = d.expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
      if (actual <= plannedTotal * 1.05) { count++; } else { break; }
    }
    return count;
  }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist streak for Dashboard access
  useEffect(() => { ls.set('streak', streak); }, [streak]);

  // ── Savings → Objective sync via localStorage ──
  useEffect(() => {
    if (!Object.keys(envLinks).length) return;
    const overrides = {};
    Object.entries(envLinks).forEach(([subName, objId]) => {
      let cum = 0;
      Object.values(savingsBlocks).forEach(block => { if (block.subs[subName]) cum += block.subs[subName].cum; });
      if (cum > 0) overrides[objId] = (overrides[objId] || 0) + cum;
    });
    ls.set('goal_overrides', overrides);
  }, [savingsBlocks, envLinks]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkSavToGoal = (subName, goalId) => {
    const l = { ...envLinks, [subName]: goalId };
    setEnvLinks(l);
    ls.set('env_links', l);
    toast.success('Lié à l\'objectif !');
  };
  const unlinkSav = (subName) => {
    const l = { ...envLinks };
    delete l[subName];
    setEnvLinks(l);
    ls.set('env_links', l);
    toast.info('Lien supprimé');
  };

  const navMonth = (dir) => setMonth(format(dir === 'prev' ? subMonths(parse(month, 'yyyy-MM', new Date()), 1) : addMonths(parse(month, 'yyyy-MM', new Date()), 1), 'yyyy-MM'));

  const addExpense = async () => {
    if (!form.amount || !form.description || !form.family || !form.category) { toast.error('Champs requis manquants'); return; }
    const exp = { id: uid(), amount: parseFloat(form.amount), date: form.date || `${month}-${format(new Date(), 'dd')}`, description: form.description, family: form.family, category: form.category, subcategory: form.subcategory || null, isFixed: false, isVariable: false, isRecurring: form.isRecurring };
    saveM(month, { ...mData, expenses: [...mData.expenses, exp] });
    if (form.isRecurring) { const rec = ls.get('recurring', []); rec.push({ description: exp.description, amount: exp.amount, day: parseInt(form.recurringDay) || 1, family: exp.family, category: exp.category, subcategory: exp.subcategory, startDate: month }); ls.set('recurring', rec); }

    // ── Auto-credit the matching savings account in Patrimoine when the expense
    //    targets an existing account name (family=Épargne & Invest, category=Épargne).
    //    For "Épargne salariale" accounts with an employerMatchPct, we also add the
    //    employer contribution (versement × pct/100) and track it cumulatively.
    //    When the target account has exactly one allocation linked to an objective, that
    //    allocation's amount is bumped by the deposit so its objective progresses without
    //    manual intervention. With 0 or multiple linked allocations the balance still
    //    grows but the user manages allocation distribution via Patrimoine.
    let creditedAccount = null;
    let creditedAbondement = 0;
    let creditedAllocation = null;
    if (form.family === 'epargne_invest' && form.category === 'Épargne' && form.subcategory) {
      const idx = patrimoineSavings.findIndex(s => s.name === form.subcategory);
      if (idx >= 0) {
        try {
          const r = await axios.get(`${API}/store/patrimoine`, hAuth);
          const blob = r.data || {};
          const savings = Array.isArray(blob.savings) ? [...blob.savings] : [];
          const srvIdx = savings.findIndex(s => s.name === form.subcategory);
          if (srvIdx >= 0) {
            const acct = savings[srvIdx];
            const pct = acct.category === 'salariale' ? (parseFloat(acct.employerMatchPct) || 0) : 0;
            const abondement = pct > 0 ? exp.amount * (pct / 100) : 0;
            const credit = exp.amount + abondement;
            let nextAllocations = Array.isArray(acct.allocations) ? acct.allocations : null;
            if (nextAllocations) {
              const linked = nextAllocations.filter(a => a.objectiveId);
              if (linked.length === 1) {
                const targetId = linked[0].id;
                nextAllocations = nextAllocations.map(a => a.id === targetId
                  ? { ...a, amount: (parseFloat(a.amount) || 0) + credit }
                  : a);
                creditedAllocation = linked[0].name;
              }
            }
            savings[srvIdx] = {
              ...acct,
              balance: (parseFloat(acct.balance) || 0) + credit,
              employerMatchTotal: (parseFloat(acct.employerMatchTotal) || 0) + abondement,
              ...(nextAllocations ? { allocations: nextAllocations } : {}),
            };
            blob.savings = savings;
            await axios.put(`${API}/store/patrimoine`, blob, hAuth);
            setPatrimoineSavings(savings);
            creditedAccount = form.subcategory;
            creditedAbondement = abondement;
          }
        } catch (e) {
          console.warn('Failed to credit savings account', e);
        }
      }
    }

    setDlgOpen(false);
    setForm({ amount: '', date: '', description: '', family: '', category: '', subcategory: '', isRecurring: false, recurringDay: '1' });
    if (creditedAccount) {
      let msg = `Dépense ajoutée — ${creditedAccount} crédité de ${fmt(exp.amount)}`;
      if (creditedAbondement > 0) msg += ` (+ ${fmt(creditedAbondement)} abondement)`;
      if (creditedAllocation) msg += ` · allocation "${creditedAllocation}" mise à jour`;
      toast.success(msg);
    } else {
      toast.success(form.isRecurring ? 'Dépense récurrente ajoutée !' : 'Dépense ajoutée !');
    }
  };

  const delExp = (exp) => {
    const id = typeof exp === 'object' ? exp.id : exp;
    const target = typeof exp === 'object' ? exp : mData.expenses.find(e => e.id === id);
    const newExpenses = mData.expenses.filter(e => e.id !== id);
    // ─── Fixed charge deletion → record per-month override (key = description)
    //     so loadMonth's budget re-sync won't bring it back next time.
    let newOverrides = mData.fixed_overrides;
    if (target?.isFixed) {
      newOverrides = { ...(mData.fixed_overrides || {}), [target.description]: { ...(mData.fixed_overrides?.[target.description] || {}), deleted: true } };
    }
    saveM(month, { ...mData, expenses: newExpenses, fixed_overrides: newOverrides });
  };
  // Recurring / fixed expenses can be overridden per-month — never modify the global budget or template.
  const updateExpAmount = (exp, newAmount) => {
    const id = typeof exp === 'object' ? exp.id : exp;
    const target = typeof exp === 'object' ? exp : mData.expenses.find(e => e.id === id);
    const newExpenses = mData.expenses.map(e => e.id === id ? { ...e, amount: newAmount } : e);
    let newOverrides = mData.fixed_overrides;
    if (target?.isFixed) {
      newOverrides = { ...(mData.fixed_overrides || {}), [target.description]: { ...(mData.fixed_overrides?.[target.description] || {}), amount: newAmount } };
    }
    saveM(month, { ...mData, expenses: newExpenses, fixed_overrides: newOverrides });
  };

  // Restore a fixed expense to its global budget template amount (clears any per-month override).
  const restoreFixedExpenseOverride = (exp) => {
    if (!exp?.isFixed) return;
    const tmpl = (budget.fixedCharges || []).find(c => c.name === exp.description);
    if (!tmpl) { toast.error('Modèle budget introuvable'); return; }
    const templateAmount = parseFloat(tmpl.amount) || 0;
    const newOverrides = { ...(mData.fixed_overrides || {}) };
    if (newOverrides[exp.description]) {
      const { amount, ...rest } = newOverrides[exp.description]; // eslint-disable-line no-unused-vars
      if (Object.keys(rest).length === 0) delete newOverrides[exp.description];
      else newOverrides[exp.description] = rest;
    }
    const newExpenses = mData.expenses.map(e => e.id === exp.id ? { ...e, amount: templateAmount } : e);
    saveM(month, { ...mData, expenses: newExpenses, fixed_overrides: newOverrides });
    toast.success(`Valeur budget restaurée : ${fmt(templateAmount)}`);
  };

  // ── Recurring expense edit logic ──
  // "Ce mois uniquement" → existing per-month behavior (override for fixed, local amount for recurring).
  // "À partir de ce mois" → freezes past months with their current amount, then updates the master
  // template (budget.fixedCharges or recurring list) so current + future months pick up the new value.
  const applyAmountChange = (exp, newAmount, scope) => {
    if (scope === 'this-month') {
      const id = exp.id;
      const newExpenses = mData.expenses.map(e => e.id === id ? { ...e, amount: newAmount } : e);
      let newOverrides = mData.fixed_overrides;
      if (exp.isFixed) {
        newOverrides = { ...(mData.fixed_overrides || {}), [exp.description]: { ...(mData.fixed_overrides?.[exp.description] || {}), amount: newAmount } };
      }
      saveM(month, { ...mData, expenses: newExpenses, fixed_overrides: newOverrides });
      toast.success(`Montant mis à jour pour ce mois : ${fmt(newAmount)}`);
      return;
    }
    // scope === 'from-now' — freeze past months first, then update the template.
    const monthly = (storeRef.current && storeRef.current.monthly) || {};
    const oldBudget = budget; // capture before any mutation, used to snapshot past months
    const oldRecurring = ls.get('recurring', []);
    if (exp.isFixed) {
      // 1. Freeze every past month that has no amount override yet on this charge, AND
      //    snapshot the old budget + recurring list into past months without snapshots,
      //    so their Analysis tab stays consistent with the pre-change planned values.
      Object.keys(monthly).forEach(m => {
        if (m >= month) return;
        const d = monthly[m];
        if (!d) return;
        let nextD = d;
        if (!nextD.budget_snapshot) nextD = { ...nextD, budget_snapshot: oldBudget };
        if (!nextD.recurring_snapshot) nextD = { ...nextD, recurring_snapshot: oldRecurring };
        const ovr = (nextD.fixed_overrides || {})[exp.description] || {};
        if (ovr.amount == null) {
          const cur = (nextD.expenses || []).find(e => e.isFixed && e.description === exp.description);
          if (cur) {
            nextD = {
              ...nextD,
              fixed_overrides: { ...(nextD.fixed_overrides || {}), [exp.description]: { ...ovr, amount: cur.amount } },
            };
          }
        }
        monthly[m] = nextD;
      });
      // 2. Clear current + future overrides on this charge so they pick the new budget value.
      Object.keys(monthly).forEach(m => {
        if (m < month) return;
        const d = monthly[m];
        if (!d || !d.fixed_overrides) return;
        const ovr = d.fixed_overrides[exp.description];
        if (!ovr || ovr.amount == null) return;
        const { amount: _drop, ...rest } = ovr; // eslint-disable-line no-unused-vars
        const newOverrides = { ...d.fixed_overrides };
        if (Object.keys(rest).length === 0) delete newOverrides[exp.description];
        else newOverrides[exp.description] = rest;
        monthly[m] = { ...d, fixed_overrides: newOverrides };
      });
      storeRef.current.monthly = monthly;
      // 3. Update the global budget template — `save` re-propagates to current + future months.
      const newBudget = {
        ...budget,
        fixedCharges: (budget.fixedCharges || []).map(c =>
          c.name === exp.description ? { ...c, amount: newAmount } : c
        ),
      };
      save(newBudget);
      toast.success(`Budget mis à jour à partir de ${month} : ${fmt(newAmount)}`);
      return;
    }
    if (exp.isRecurring) {
      // 1. Snapshot the old budget + recurring list into past months that lack a snapshot,
      //    so historical Analysis (Prévu) keeps the original recurring amounts.
      Object.keys(monthly).forEach(m => {
        if (m >= month) return;
        const d = monthly[m];
        if (!d) return;
        let nextD = d;
        if (!nextD.budget_snapshot) nextD = { ...nextD, budget_snapshot: oldBudget };
        if (!nextD.recurring_snapshot) nextD = { ...nextD, recurring_snapshot: oldRecurring };
        if (nextD !== d) monthly[m] = nextD;
      });
      // 2. Update the master recurring list.
      const updated = oldRecurring.map(r => r.description === exp.description ? { ...r, amount: newAmount } : r);
      ls.set('recurring', updated);
      // 3. Update existing current + future month copies in place.
      Object.keys(monthly).forEach(m => {
        if (m < month) return;
        const d = monthly[m];
        if (!d) return;
        const newExpenses = (d.expenses || []).map(e =>
          (e.isRecurring && e.description === exp.description) ? { ...e, amount: newAmount } : e
        );
        monthly[m] = { ...d, expenses: newExpenses };
      });
      storeRef.current.monthly = monthly;
      const cur = monthly[month];
      if (cur) setMData(cur);
      persistStore();
      toast.success(`Récurrente mise à jour à partir de ${month} : ${fmt(newAmount)}`);
    }
  };

  // Apply an optional end date to a recurring expense. Past months are NEVER touched —
  // they keep their snapshot. Future months past `endDate` are cleared (deleted override for
  // fixed charges, expense removed for recurring list items).
  // endDateRaw must be either an empty string (clear) or a strict ISO date "YYYY-MM-DD".
  // Any malformed input is rejected to prevent destructive month-wide deletions.
  const applyEndDate = (exp, endDateRaw) => {
    let endDate = null;
    if (endDateRaw && String(endDateRaw).trim()) {
      const v = String(endDateRaw).trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        toast.error('Date invalide (format attendu : JJ/MM/AAAA)');
        return;
      }
      const parsed = new Date(`${v}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) {
        toast.error('Date invalide');
        return;
      }
      endDate = v; // canonical ISO yyyy-MM-dd
    }
    // For month-based comparisons we only need the YYYY-MM prefix.
    const endMonth = endDate ? endDate.slice(0, 7) : null;
    if (exp.isFixed) {
      const newBudget = {
        ...budget,
        fixedCharges: (budget.fixedCharges || []).map(c =>
          c.name === exp.description ? { ...c, endDate } : c
        ),
      };
      save(newBudget);
    } else if (exp.isRecurring) {
      const rec = ls.get('recurring', []);
      const updated = rec.map(r => r.description === exp.description ? { ...r, endDate } : r);
      ls.set('recurring', updated);
    }
    // Clean future months past the end month (YYYY-MM granularity).
    if (endMonth) {
      const monthly = (storeRef.current && storeRef.current.monthly) || {};
      Object.keys(monthly).forEach(m => {
        if (m <= endMonth) return;
        const d = monthly[m];
        if (!d) return;
        if (exp.isFixed) {
          monthly[m] = {
            ...d,
            expenses: (d.expenses || []).filter(e => !(e.isFixed && e.description === exp.description)),
            fixed_overrides: { ...(d.fixed_overrides || {}), [exp.description]: { ...(d.fixed_overrides?.[exp.description] || {}), deleted: true } },
          };
        } else if (exp.isRecurring) {
          monthly[m] = {
            ...d,
            expenses: (d.expenses || []).filter(e => !(e.isRecurring && e.description === exp.description)),
          };
        }
      });
      storeRef.current.monthly = monthly;
      if (month > endMonth) {
        const cur = monthly[month];
        if (cur) setMData(cur);
      }
      persistStore();
    }
    if (endDate) {
      try {
        const display = format(new Date(`${endDate}T00:00:00`), 'dd/MM/yyyy');
        toast.success(`Date de fin appliquée : ${display}`);
      } catch {
        toast.success(`Date de fin appliquée : ${endDate}`);
      }
    } else {
      toast.success('Date de fin supprimée');
    }
  };

  // Read current end date for a recurring expense from its master template.
  const getRecEndDate = (exp) => {
    if (exp?.isFixed) {
      const c = (budget.fixedCharges || []).find(x => x.name === exp.description);
      return c?.endDate || '';
    }
    if (exp?.isRecurring) {
      const rec = ls.get('recurring', []);
      const r = rec.find(x => x.description === exp.description);
      return r?.endDate || '';
    }
    return '';
  };

  const addSub = (fam, cat) => { const n = prompt('Nom de la sous-catégorie :'); if (!n) return; const c = { ...customSubs }; if (!c[fam]) c[fam] = {}; if (!c[fam][cat]) c[fam][cat] = []; c[fam][cat].push(n); setCustomSubs(c); ls.set('custom_subs', c); };

  const addCustomCat = (name) => {
    if (!name) return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    if (customCats.includes(trimmed) || CHARGE_CATS.includes(trimmed)) { toast.info('Catégorie déjà existante'); return trimmed; }
    const updated = [...customCats, trimmed];
    setCustomCats(updated);
    ls.set('custom_cats', updated);
    toast.success(`Catégorie "${trimmed}" créée`);
    return trimmed;
  };

  const removeCustomCat = (name) => {
    if (!window.confirm(`Supprimer la catégorie "${name}" ?\nLes dépenses associées ne seront pas supprimées.`)) return;
    const updated = customCats.filter(c => c !== name);
    setCustomCats(updated);
    ls.set('custom_cats', updated);
    toast.success(`Catégorie "${name}" supprimée`);
  };

  const removeCustomSub = (fam, cat, sub) => {
    if (!window.confirm(`Supprimer la sous-catégorie "${sub}" ?\nLes dépenses associées ne seront pas supprimées.`)) return;
    const c = { ...customSubs };
    if (c[fam]?.[cat]) {
      c[fam][cat] = c[fam][cat].filter(s => s !== sub);
      if (c[fam][cat].length === 0) delete c[fam][cat];
    }
    setCustomSubs(c);
    ls.set('custom_subs', c);
    toast.success(`Sous-catégorie "${sub}" supprimée`);
  };

  const allChargeCats = [...CHARGE_CATS, ...customCats.filter(c => !CHARGE_CATS.includes(c))];

  const getAdvice = () => {
    const t = profile?.profile_type;
    const r = totalRevM > 0 ? (famTotals.epargne_invest / totalRevM) * 100 : 0;
    if (t === 'architecte') return r >= 20 ? { t: "Excellent ! Votre discipline d'épargne est exemplaire. Continuez à bâtir vos fondations.", i: '🏗️' } : { t: "Architecte, renforcez votre épargne de sécurité. La stabilité est votre force.", i: '🏗️' };
    if (t === 'stratege') return famTotals.non_essentiel / totalRevM > 0.3 ? { t: "Stratège, vos envies dépassent 30%. Optimisez pour réallouer vers l'investissement.", i: '♟️' } : { t: "Bonne maîtrise stratège ! Diversifiez vos placements pour maximiser le rendement.", i: '♟️' };
    if (t === 'pilote') return r >= 25 ? { t: "Pilote, votre taux d'investissement est fort ! Vérifiez votre épargne de sécurité.", i: '🚀' } : { t: "Pilote, augmentez votre exposition aux marchés. Chaque euro investi compte.", i: '🚀' };
    return { t: "Configurez votre profil investisseur pour des conseils personnalisés.", i: '💡' };
  };

  // Budget computed
  const bTotalRev = budget.revenues.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const bTotalFixed = budget.fixedCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const bTotalVar = budget.variableCharges.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const bSurplus = bTotalRev - bTotalFixed - bTotalVar;
  const bTotalEnv = budget.envelopes.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  const curCats = form.family ? getCats(form.family) : {};
  const curSubs = form.category && curCats[form.category] ? curCats[form.category] : [];

  // ═══════ RENDER ═══════
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="finances-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h1 className="font-display text-3xl font-bold text-white">Gestion Financière</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 text-xs" onClick={() => setShowResetConfirm(true)} data-testid="reset-finances-btn"><Trash2 className="w-3.5 h-3.5 mr-1.5" />Réinitialiser</Button>
          <Button onClick={() => { setTab('month'); setTimeout(() => setDlgOpen(true), 150); }} className="bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover" data-testid="add-expense-btn"><Plus className="w-5 h-5 mr-2" /> Nouvelle dépense</Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {[
          { label: 'Revenus du mois', value: totalRevM, color: 'text-cap-emerald', icon: Receipt, bg: 'bg-cap-emerald/10' },
          { label: 'Dépenses du mois', value: totalExpM, color: 'text-white', icon: TrendingDown, bg: 'bg-cap-error/10' },
          { label: 'Reste à allouer', value: reste, color: reste >= 0 ? 'text-cap-blue' : 'text-cap-error', icon: PieChart, bg: 'bg-cap-blue/10' }
        ].map((kpi, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="glass-card p-5" data-testid={`kpi-${i}`}>
            <div className="flex items-center gap-3 mb-1"><div className={`p-2 rounded-lg ${kpi.bg}`}><kpi.icon className={`w-5 h-5 ${kpi.color}`} /></div><span className="text-cap-text-secondary text-sm">{kpi.label}</span></div>
            <p className={`font-display text-2xl font-bold ${kpi.color}`}>{fmt(kpi.value)}</p>
          </motion.div>
        ))}
      </div>

      {/* ═══ MONTHLY RECAP DIALOG ═══ */}
      <Dialog open={recapOpen} onOpenChange={(open) => { if (!open) dismissRecap(); }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 max-w-lg p-0 overflow-hidden" data-testid="recap-dialog">
          {recap && <RecapContent recap={recap} prevMonthLabel={prevMonthLabel} onClose={dismissRecap} />}
        </DialogContent>
      </Dialog>

      <Dialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <DialogContent className="bg-[#1a1d27] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle>Réinitialiser les données</DialogTitle></DialogHeader>
          <p className="text-cap-text-secondary text-sm mt-2">Êtes-vous sûr de vouloir réinitialiser toutes les données de cette section ? Cette action est irréversible.</p>
          <div className="flex items-center justify-end gap-3 mt-4">
            <Button variant="outline" size="sm" className="border-white/10 text-cap-text-muted hover:text-white" onClick={() => setShowResetConfirm(false)}>Annuler</Button>
            <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={handleResetFinances} data-testid="confirm-reset-finances">Réinitialiser</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <TabsList className="bg-cap-bg-elevated border border-white/5 flex-wrap">
          {[['budget', 'Mon Budget'], ['month', 'Mon Mois'], ['analysis', 'Mon Analyse'], ['savings', 'Mon Épargne'], ['history', 'Historique']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="data-[state=active]:bg-cap-emerald/20 data-[state=active]:text-cap-emerald" data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        {/* ═══ TAB 1: MON BUDGET ═══ */}
        <TabsContent value="budget" data-testid="budget-content">
          {!editing && ls.get('budget', null) ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between"><h2 className="font-display text-xl font-semibold text-white">Ma constitution financière</h2><Button variant="outline" onClick={() => { setEditing(true); setStep(1); }} className="border-white/10 text-white hover:bg-white/5" data-testid="edit-budget"><Pencil className="w-4 h-4 mr-2" /> Modifier</Button></div>
              <div className="glass-card p-6" data-testid="sankey-view"><SankeyChart revenues={budget.revenues} fixedCharges={budget.fixedCharges} variableCharges={budget.variableCharges} envelopes={budget.envelopes} /></div>
              {/* Legend */}
              <div className="flex flex-wrap gap-3 px-1">
                {[['Charges fixes', DEST_COLORS.fixed, '🔒'], ['Charges variables', DEST_COLORS.variable, '📊'], ['Épargne', DEST_COLORS.epargne, ''], ['Investissement', DEST_COLORS.investissement, ''], ['Provision', DEST_COLORS.provision, '']].map(([l, c, icon]) => (
                  <div key={l} className="flex items-center gap-1.5 text-xs"><div className="w-3 h-3 rounded" style={{ backgroundColor: c }} /><span className="text-cap-text-muted">{icon} {l}</span></div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="glass-card p-4"><p className="text-cap-text-muted text-xs mb-1">Revenus</p><p className="font-display text-lg font-bold text-cap-emerald">{fmt(bTotalRev)}</p></div>
                <div className="glass-card p-4"><p className="text-cap-text-muted text-xs mb-1">Charges fixes</p><p className="font-display text-lg font-bold text-cap-error">{fmt(bTotalFixed)}</p></div>
                <div className="glass-card p-4"><p className="text-cap-text-muted text-xs mb-1">Charges variables</p><p className="font-display text-lg font-bold text-yellow-400">{fmt(bTotalVar)}</p></div>
                <div className="glass-card p-4"><p className="text-cap-text-muted text-xs mb-1">Surplus</p><p className="font-display text-lg font-bold text-cap-blue">{fmt(bSurplus)}</p></div>
              </div>
            </div>
          ) : (
            /* ── WIZARD ── */
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                {[1, 2, 3].map(s => (
                  <button key={s} onClick={() => setStep(s)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${step === s ? 'bg-cap-emerald/20 text-cap-emerald' : step > s ? 'bg-white/5 text-cap-emerald' : 'bg-white/5 text-cap-text-muted'}`}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step >= s ? 'bg-cap-emerald text-cap-bg' : 'bg-white/10 text-cap-text-muted'}`}>{s}</span>
                    {s === 1 ? 'Revenus' : s === 2 ? 'Charges' : 'Enveloppes'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="glass-card p-6">
                  {/* STEP 1 */}
                  {step === 1 && (
                    <div data-testid="step-1">
                      <h3 className="font-display text-lg font-semibold text-white mb-1">Mes revenus habituels</h3>
                      <p className="text-cap-text-muted text-sm mb-5">Indiquez vos revenus habituels. Vous pourrez ajuster le montant réel chaque mois.</p>
                      <div className="space-y-3 mb-4">
                        {budget.revenues.map((r, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-cap-bg rounded-lg">
                            <span className="text-white text-sm">{r.name}</span>
                            <div className="flex items-center gap-2"><span className="text-cap-emerald font-semibold text-sm">{fmt(r.amount)}</span><button onClick={() => save({ ...budget, revenues: budget.revenues.filter((_, j) => j !== i) })} className="p-1 text-cap-text-muted hover:text-cap-error"><Trash2 className="w-3.5 h-3.5" /></button></div>
                          </div>
                        ))}
                      </div>
                      <InlineAdd placeholder="Source (ex: Salaire)" onAdd={(n, a) => save({ ...budget, revenues: [...budget.revenues, { name: n, amount: parseFloat(a) }] })} color="emerald" testId="rev" />
                      <div className="mt-4 flex justify-end"><Button onClick={() => setStep(2)} className="bg-cap-emerald text-cap-bg font-semibold">Suivant <ChevronRight className="w-4 h-4 ml-1" /></Button></div>
                    </div>
                  )}
                  {/* STEP 2 */}
                  {step === 2 && (
                    <div data-testid="step-2" className="space-y-6">
                      <div>
                        <h3 className="font-display text-lg font-semibold text-white mb-1">Mes charges mensuelles</h3>
                        <p className="text-cap-text-muted text-sm mb-4">Surplus = Revenus - Charges fixes - Charges variables</p>
                      </div>
                      {/* Fixed charges */}
                      <div>
                        <div className="flex items-center gap-2 mb-3"><span className="text-lg">🔒</span><div><p className="text-white font-medium text-sm">Charges fixes</p><p className="text-cap-text-muted text-xs">Identiques chaque mois</p></div></div>
                        <div className="space-y-3 mb-3">
                          {budget.fixedCharges.map((c, i) => (
                            <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg ${c.isDebtLinked ? 'bg-cap-blue/5 border border-cap-blue/20' : 'bg-cap-bg'}`}>
                              <div className="flex items-center gap-2">
                                {c.isDebtLinked && <span className="text-cap-blue text-xs" title="Crédit lié — modifier dans Patrimoine → Dettes">🔗</span>}
                                <span className="text-white text-sm">{c.name}</span>
                                <span className="text-cap-text-muted text-xs">{c.category}</span>
                                {c.isDebtLinked && <span className="text-cap-blue/60 text-[10px]">Crédit lié</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-cap-error font-semibold text-sm">-{fmt(c.amount)}</span>
                                {!c.isDebtLinked && <button onClick={() => save({ ...budget, fixedCharges: budget.fixedCharges.filter((_, j) => j !== i) })} className="p-1 text-cap-text-muted hover:text-cap-error"><Trash2 className="w-3.5 h-3.5" /></button>}
                              </div>
                            </div>
                          ))}
                        </div>
                        <ChargeAdd onAdd={(n, a, cat) => save({ ...budget, fixedCharges: [...budget.fixedCharges, { name: n, amount: parseFloat(a), category: cat, startDate: month }] })} color="red" testId="fc" allCats={allChargeCats} onAddCat={addCustomCat} onRemoveCat={removeCustomCat} />
                      </div>
                      {/* Variable charges */}
                      <div>
                        <div className="flex items-center gap-2 mb-3"><span className="text-lg">📊</span><div><p className="text-white font-medium text-sm">Charges variables</p><p className="text-cap-text-muted text-xs">Estimées en moyenne mensuelle</p></div></div>
                        <div className="space-y-3 mb-3">
                          {budget.variableCharges.map((c, i) => (
                            <div key={i} className="flex items-center justify-between p-2.5 bg-cap-bg rounded-lg">
                              <div><span className="text-white text-sm">{c.name}</span><span className="text-cap-text-muted text-xs ml-2">{c.category}</span></div>
                              <div className="flex items-center gap-2"><span className="text-yellow-400 font-semibold text-sm">~{fmt(c.amount)}</span><button onClick={() => save({ ...budget, variableCharges: budget.variableCharges.filter((_, j) => j !== i) })} className="p-1 text-cap-text-muted hover:text-cap-error"><Trash2 className="w-3.5 h-3.5" /></button></div>
                            </div>
                          ))}
                        </div>
                        <ChargeAdd onAdd={(n, a, cat) => save({ ...budget, variableCharges: [...budget.variableCharges, { name: n, amount: parseFloat(a), category: cat }] })} color="yellow" testId="vc" allCats={allChargeCats} onAddCat={addCustomCat} onRemoveCat={removeCustomCat} />
                      </div>
                      {/* Surplus */}
                      <div className="p-3 bg-cap-bg rounded-lg flex justify-between items-center">
                        <span className="text-cap-text-muted text-sm">Surplus disponible</span>
                        <span className={`font-display font-bold ${bSurplus >= 0 ? 'text-cap-emerald' : 'text-cap-error'}`}>{fmt(bSurplus)}</span>
                      </div>
                      <div className="flex justify-between"><Button variant="outline" onClick={() => setStep(1)} className="border-white/10 text-white"><ChevronLeft className="w-4 h-4 mr-1" /> Retour</Button><Button onClick={() => setStep(3)} className="bg-cap-emerald text-cap-bg font-semibold">Suivant <ChevronRight className="w-4 h-4 ml-1" /></Button></div>
                    </div>
                  )}
                  {/* STEP 3 */}
                  {step === 3 && (
                    <div data-testid="step-3">
                      <h3 className="font-display text-lg font-semibold text-white mb-1">Répartition du surplus</h3>
                      <p className="text-cap-text-muted text-sm mb-5">Surplus : <span className="text-cap-emerald font-semibold">{fmt(bSurplus)}</span> — Non alloué : <span className={bSurplus - bTotalEnv > 0 ? 'text-cap-blue' : 'text-cap-error'}>{fmt(bSurplus - bTotalEnv)}</span></p>
                      <div className="space-y-3 mb-4">
                        {budget.envelopes.map((e, i) => (
                          <div key={i} className="flex items-center justify-between p-2.5 bg-cap-bg rounded-lg">
                            <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DEST_COLORS[e.type] }} /><span className="text-white text-sm">{e.name}</span><span className="text-cap-text-muted text-xs capitalize">{e.type}</span></div>
                            <div className="flex items-center gap-2"><span className="font-semibold text-sm" style={{ color: DEST_COLORS[e.type] }}>{fmt(e.amount)}</span><button onClick={() => save({ ...budget, envelopes: budget.envelopes.filter((_, j) => j !== i) })} className="p-1 text-cap-text-muted hover:text-cap-error"><Trash2 className="w-3.5 h-3.5" /></button></div>
                          </div>
                        ))}
                      </div>
                      <EnvForm onAdd={(env) => { if (bTotalEnv + parseFloat(env.amount) > bSurplus + 0.01) { toast.error('Dépasse le surplus !'); return; } save({ ...budget, envelopes: [...budget.envelopes, env] }); }} />
                      <div className="mt-4 flex justify-between">
                        <Button variant="outline" onClick={() => setStep(2)} className="border-white/10 text-white"><ChevronLeft className="w-4 h-4 mr-1" /> Retour</Button>
                        <Button onClick={() => { save(budget); setEditing(false); toast.success('Budget enregistré !'); }} className="bg-cap-emerald text-cap-bg font-semibold" data-testid="save-budget"><Save className="w-4 h-4 mr-2" /> Enregistrer</Button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Live Sankey preview */}
                <div className="glass-card p-6 flex flex-col">
                  <h3 className="font-display text-sm font-semibold text-cap-text-secondary mb-3">Aperçu en temps réel</h3>
                  <SankeyChart revenues={budget.revenues} fixedCharges={budget.fixedCharges} variableCharges={budget.variableCharges} envelopes={budget.envelopes} />
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ═══ TAB 2: MON MOIS ═══ */}
        <TabsContent value="month" data-testid="month-content">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navMonth('prev')} className="text-cap-text-secondary hover:text-white" data-testid="prev-m"><ChevronLeft className="w-5 h-5" /></Button>
            <span className="text-white font-display font-semibold text-lg min-w-[160px] text-center capitalize" data-testid="m-label">{format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy', { locale: fr })}</span>
            <Button variant="ghost" size="icon" onClick={() => navMonth('next')} className="text-cap-text-secondary hover:text-white" data-testid="next-m"><ChevronRight className="w-5 h-5" /></Button>
          </div>
          {/* Revenus */}
          <div className="glass-card p-5 mb-6" data-testid="month-rev">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display text-base font-semibold text-white flex items-center gap-2"><Wallet className="w-4 h-4 text-cap-emerald" /> Revenus du mois</h3><span className="font-display text-lg font-bold text-cap-emerald">{fmt(totalRevM)}</span></div>
            <div className="space-y-3 mb-3">
              {mData.revenues.map((r, i) => (
                <div key={r.id || i} className="flex items-center justify-between p-2.5 bg-cap-bg rounded-lg">
                  <div className="flex items-center gap-2"><span className="text-white text-sm">{r.name}</span>{r.isBudget && <span className="text-cap-text-muted text-xs">(budget)</span>}</div>
                  {r.isBudget ? (
                    <span className="text-cap-emerald font-semibold text-sm w-24 text-right pr-3" data-testid={`m-rev-budget-${i}`}>{fmt(r.amount)}</span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={r.amount}
                        onChange={e => {
                          const newAmount = parseFloat(e.target.value) || 0;
                          const extras = (mData.extra_incomes || []).map(x => x.id === r.id ? { ...x, amount: newAmount } : x);
                          const revs = mData.revenues.map((rv, j) => j === i ? { ...rv, amount: newAmount } : rv);
                          saveM(month, { ...mData, extra_incomes: extras, revenues: revs });
                        }}
                        className="bg-cap-bg-elevated border-white/10 text-cap-emerald text-right text-sm w-24 h-8 font-semibold"
                        data-testid={`m-rev-extra-${i}`}
                      />
                      <button
                        onClick={() => {
                          const extras = (mData.extra_incomes || []).filter(x => x.id !== r.id);
                          const revs = mData.revenues.filter((_, j) => j !== i);
                          saveM(month, { ...mData, extra_incomes: extras, revenues: revs });
                        }}
                        className="p-1 text-cap-text-muted hover:text-cap-error"
                        data-testid={`m-rev-extra-del-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2"><Input placeholder="Revenu exceptionnel" className="bg-cap-bg border-white/10 text-white text-sm" id="mr-n" /><Input type="number" placeholder="€" className="bg-cap-bg border-white/10 text-white text-sm w-24" id="mr-a" /><Button size="sm" onClick={() => { const n = document.getElementById('mr-n').value; const a = document.getElementById('mr-a').value; if (n && a) { const newExtra = { id: uid(), name: n, amount: parseFloat(a) }; const newExtras = [...(mData.extra_incomes || []), newExtra]; const newRev = [...mData.revenues, { ...newExtra, isBudget: false }]; saveM(month, { ...mData, extra_incomes: newExtras, revenues: newRev }); document.getElementById('mr-n').value = ''; document.getElementById('mr-a').value = ''; } }} className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30 shrink-0" data-testid="add-m-rev"><Plus className="w-4 h-4" /></Button></div>
          </div>
          {/* Dépenses 2 colonnes */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 glass-card" data-testid="exp-list">
              <div className="p-4 border-b border-white/5 flex items-center justify-between"><h3 className="font-display text-base font-semibold text-white">Dépenses du mois</h3><Button size="sm" onClick={() => setDlgOpen(true)} className="bg-cap-emerald/20 text-cap-emerald hover:bg-cap-emerald/30" data-testid="open-exp-form"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button></div>
              <ScrollArea className="h-[400px]">
                {mData.expenses.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {mData.expenses.map(exp => (
                      <div key={exp.id} className="p-3 flex items-center justify-between hover:bg-white/[0.03] transition-colors" data-testid={`exp-${exp.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-cap-bg flex items-center justify-center shrink-0"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: FAMILY_META[exp.family]?.color || '#94a3b8' }} /></div>
                          <div className="min-w-0"><p className="text-white text-sm flex items-center gap-1.5 truncate">{(exp.isFixed || exp.isVariable || exp.isRecurring) && <span title="Récurrente">🔄</span>}{exp.description}</p><p className="text-cap-text-muted text-xs truncate">{exp.category}{exp.subcategory ? ` · ${exp.subcategory}` : ''}</p></div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            {(exp.isRecurring || exp.isFixed) ? (() => {
                              const tmpl = exp.isFixed ? (budget.fixedCharges || []).find(c => c.name === exp.description) : null;
                              const tmplAmount = tmpl ? (parseFloat(tmpl.amount) || 0) : null;
                              const hasOverride = exp.isFixed && tmplAmount != null && Math.abs((parseFloat(exp.amount) || 0) - tmplAmount) > 0.001;
                              const curEndDate = getRecEndDate(exp);
                              return (
                                <div className="flex items-center justify-end gap-1.5">
                                  {hasOverride && (
                                    <TooltipProvider delayDuration={150}>
                                      <UITooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            type="button"
                                            onClick={() => restoreFixedExpenseOverride(exp)}
                                            className="p-1 rounded text-cap-warning hover:text-cap-emerald hover:bg-white/5 transition-colors"
                                            data-testid={`m-exp-restore-${exp.id}`}
                                            aria-label="Restaurer la valeur du budget"
                                          >
                                            <RotateCcw className="w-3.5 h-3.5" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="bg-cap-bg-elevated border-white/10 text-white text-xs">
                                          <span>Valeur budget : {fmt(tmplAmount)} — cliquez pour restaurer</span>
                                        </TooltipContent>
                                      </UITooltip>
                                    </TooltipProvider>
                                  )}
                                  <TooltipProvider delayDuration={150}>
                                    <UITooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            // Normalize legacy YYYY-MM values to YYYY-MM-DD (default to the 1st)
                                            // so the native date input renders them correctly.
                                            const v = String(curEndDate || '');
                                            const normalized = /^\d{4}-\d{2}$/.test(v) ? `${v}-01` : (/^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '');
                                            setRecOptionsModal({ open: true, exp, endDate: normalized });
                                          }}
                                          className={`p-1 rounded hover:bg-white/5 transition-colors ${curEndDate ? 'text-cap-blue' : 'text-cap-text-muted hover:text-white'}`}
                                          data-testid={`m-exp-enddate-${exp.id}`}
                                          aria-label="Définir une date de fin"
                                        >
                                          <CalendarRange className="w-3.5 h-3.5" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="bg-cap-bg-elevated border-white/10 text-white text-xs">
                                        <span>{curEndDate ? `Date de fin : ${(() => { try { const s = String(curEndDate); const d = s.length === 7 ? new Date(`${s}-01T00:00:00`) : new Date(`${s}T00:00:00`); return format(d, 'dd/MM/yyyy'); } catch { return curEndDate; } })()}` : 'Définir une date de fin (optionnel)'}</span>
                                      </TooltipContent>
                                    </UITooltip>
                                  </TooltipProvider>
                                  <p className={`font-semibold text-sm w-24 text-right ${hasOverride ? 'text-cap-warning' : 'text-cap-error'}`} data-testid={`m-exp-rec-${exp.id}`}>
                                    -{fmt(exp.amount)}
                                  </p>
                                  <TooltipProvider delayDuration={150}>
                                    <UITooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={() => setRecAmountModal({ open: true, exp, oldAmount: parseFloat(exp.amount) || 0, newAmount: parseFloat(exp.amount) || 0 })}
                                          className="p-1 rounded text-cap-text-muted hover:text-cap-emerald hover:bg-white/5 transition-colors"
                                          data-testid={`m-exp-edit-${exp.id}`}
                                          aria-label="Modifier le montant"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="bg-cap-bg-elevated border-white/10 text-white text-xs">
                                        <span>Modifier le montant</span>
                                      </TooltipContent>
                                    </UITooltip>
                                  </TooltipProvider>
                                </div>
                              );
                            })() : (
                              <p className="text-cap-error font-semibold text-sm">-{fmt(exp.amount)}</p>
                            )}
                            <p className="text-cap-text-muted text-xs">{exp.date?.split('-').reverse().slice(0, 2).join('/')}</p>
                          </div>
                          <button onClick={() => delExp(exp)} className="p-1 text-cap-text-muted hover:text-cap-error" data-testid={`m-exp-del-${exp.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <div className="flex items-center justify-center h-full text-cap-text-muted py-16">Aucune dépense ce mois</div>}
              </ScrollArea>
            </div>
            <div className="lg:col-span-2 space-y-4" data-testid="cat-totals">
              <div className="glass-card p-5">
                <h3 className="font-display text-sm font-semibold text-white mb-3">Totaux par catégorie</h3>
                {catTotals.length > 0 ? <div className="space-y-3">{catTotals.map(([cat, amt], idx) => (<button key={cat} onClick={() => setSelCat(selCat === cat ? null : cat)} className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-all text-left ${selCat === cat ? 'bg-cap-emerald/10 ring-1 ring-cap-emerald/30' : 'bg-cap-bg hover:bg-white/5'}`} data-testid={`cat-${cat}`}><div className="flex items-center gap-2 min-w-0"><div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[idx % PIE_COLORS.length] }} /><span className="text-white text-sm truncate">{cat}</span></div><span className="text-cap-text-secondary font-semibold text-sm shrink-0 ml-2">{fmt(amt)}</span></button>))}<div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between px-2.5" data-testid="cat-total-general"><span className="text-white text-sm font-semibold">Total dépensé ce mois</span><span className="text-cap-error font-display text-base font-bold">{fmt(totalExpM)}</span></div></div> : <p className="text-cap-text-muted text-sm text-center py-4">Aucune donnée</p>}
              </div>
              {selCat && catHist.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-5" data-testid="cat-drill">
                  <h3 className="font-display text-sm font-semibold text-white mb-1">{selCat}</h3><p className="text-cap-text-muted text-xs mb-3">Évolution 6 mois</p>
                  <div className="h-40"><ResponsiveContainer width="100%" height="100%"><BarChart data={catHist}><XAxis dataKey="label" stroke="#64748b" fontSize={10} /><YAxis stroke="#64748b" fontSize={10} tickFormatter={v => `${v}€`} /><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={v => [fmt(v), selCat]} /><Bar dataKey="amount" fill="#4f8eff" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
                </motion.div>
              )}
            </div>
          </div>
          {/* Expense dialog */}
          <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
            <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-md">
              <DialogHeader><DialogTitle className="font-display text-lg">Ajouter une dépense</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Montant</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="50" className="bg-cap-bg border-white/10 text-white" data-testid="exp-amt" /></div>
                  <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-cap-bg border-white/10 text-white" /></div>
                </div>
                <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Courses Carrefour" className="bg-cap-bg border-white/10 text-white" data-testid="exp-desc" /></div>
                <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Famille</Label>
                  <Select value={form.family} onValueChange={v => setForm({ ...form, family: v, category: '', subcategory: '' })}><SelectTrigger className="bg-cap-bg border-white/10 text-white" data-testid="exp-fam"><SelectValue placeholder="Famille" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{Object.entries(FAMILY_META).map(([k, v]) => <SelectItem key={k} value={k}><span className="flex items-center gap-2">{v.emoji} {v.label}</span></SelectItem>)}</SelectContent></Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Catégorie</Label><Select value={form.category} onValueChange={v => { if (v === '__add_cat__') { const n = prompt('Nom de la nouvelle catégorie :'); if (n) { const created = addCustomCat(n); if (created) setForm({ ...form, category: created, subcategory: '' }); } } else { setForm({ ...form, category: v, subcategory: '' }); } }} disabled={!form.family}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue placeholder="Catégorie" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{Object.keys(curCats).map(c => <SelectItem key={c} value={c} className="group"><div className="flex items-center justify-between w-full">{c}{customCats.includes(c) && <span onPointerDown={e => { e.stopPropagation(); e.preventDefault(); removeCustomCat(c); }} className="opacity-0 group-hover:opacity-100 ml-2 text-cap-text-muted hover:text-cap-error transition-opacity cursor-pointer"><Trash2 className="w-3 h-3" /></span>}</div></SelectItem>)}<SelectItem value="__add_cat__"><span className="text-cap-emerald">+ Créer une catégorie</span></SelectItem></SelectContent></Select></div>
                  <div className="space-y-1"><Label className="text-cap-text-secondary text-xs">Sous-catégorie</Label><Select value={form.subcategory} onValueChange={v => { if (v === '__add__') addSub(form.family, form.category); else setForm({ ...form, subcategory: v }); }} disabled={!form.category}><SelectTrigger className="bg-cap-bg border-white/10 text-white"><SelectValue placeholder="Optionnel" /></SelectTrigger><SelectContent className="bg-cap-bg-elevated border-white/10">{curSubs.map(s => <SelectItem key={s} value={s} className="group"><div className="flex items-center justify-between w-full">{s}{(customSubs[form.family]?.[form.category] || []).includes(s) && <span onPointerDown={e => { e.stopPropagation(); e.preventDefault(); removeCustomSub(form.family, form.category, s); }} className="opacity-0 group-hover:opacity-100 ml-2 text-cap-text-muted hover:text-cap-error transition-opacity cursor-pointer"><Trash2 className="w-3 h-3" /></span>}</div></SelectItem>)}<SelectItem value="__add__"><span className="text-cap-emerald">+ Ajouter</span></SelectItem></SelectContent></Select></div>
                </div>
                <div className="p-2.5 bg-cap-bg rounded-lg border border-white/5 space-y-2">
                  <div className="flex items-center gap-2.5"><Checkbox id="rec" checked={form.isRecurring} onCheckedChange={v => setForm({ ...form, isRecurring: !!v })} className="border-white/20 data-[state=checked]:bg-cap-emerald data-[state=checked]:border-cap-emerald" data-testid="exp-rec" /><Label htmlFor="rec" className="text-cap-text-secondary cursor-pointer text-sm">Dépense récurrente</Label></div>
                  {form.isRecurring && <div className="flex items-center gap-2 text-sm"><span className="text-cap-text-muted">Prélevée le</span><Input type="number" min="1" max="31" value={form.recurringDay} onChange={e => setForm({ ...form, recurringDay: e.target.value })} className="bg-cap-bg-elevated border-white/10 text-white w-14 h-7 text-center text-sm" data-testid="exp-rec-day" /><span className="text-cap-text-muted">du mois</span></div>}
                </div>
                <Button onClick={addExpense} className="w-full bg-cap-emerald text-cap-bg font-semibold hover:bg-cap-emerald-hover" data-testid="submit-exp">Ajouter</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═══ TAB 3: MON ANALYSE ═══ */}
        <TabsContent value="analysis" data-testid="analysis-content">
          <div className="space-y-6">
            {/* Month header */}
            <div className="flex items-center gap-3" data-testid="analysis-month-header">
              <CalendarCheck className="w-5 h-5 text-cap-emerald" />
              <h2 className="font-display text-lg font-semibold text-white capitalize">
                Analyse — {format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy', { locale: fr })}
              </h2>
            </div>
            {/* Comparison */}
            <div className="glass-card p-6" data-testid="comparison">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Comparaison Budget vs Réel</h3>
              {comparison.length > 0 ? <div className="space-y-3">{comparison.map((it, i) => {
                const isExpense = it.isExpense;
                const absDiff = Math.abs(it.diff);
                const pctOff = it.planned > 0 ? (absDiff / it.planned) * 100 : 0;
                let status, Ic, cl;
                if (isExpense) {
                  if (it.real <= it.planned * 1.02) { status = 'ok'; Ic = CheckCircle; cl = 'text-cap-emerald'; }
                  else if (pctOff <= 20) { status = 'warn'; Ic = AlertTriangle; cl = 'text-yellow-400'; }
                  else { status = 'over'; Ic = XCircle; cl = 'text-cap-error'; }
                } else {
                  if (it.real >= it.planned * 0.98) { status = 'ok'; Ic = CheckCircle; cl = 'text-cap-emerald'; }
                  else if (it.real >= it.planned * 0.7) { status = 'warn'; Ic = AlertTriangle; cl = 'text-yellow-400'; }
                  else { status = 'over'; Ic = XCircle; cl = 'text-cap-error'; }
                }
                const diffCl = isExpense
                  ? (it.diff > 0 ? 'text-cap-error' : 'text-cap-emerald')
                  : (it.diff >= 0 ? 'text-cap-emerald' : 'text-cap-error');
                const dotColor = isExpense ? '#f59e0b' : '#00e5a0';
                return (
                <div key={i} className="flex items-center justify-between p-3 bg-cap-bg rounded-lg flex-wrap gap-2" data-testid={`comparison-row-${i}`}>
                  <div className="flex items-center gap-2.5"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dotColor }} /><span className="text-white text-sm font-medium">{it.name}</span><span className="text-cap-text-muted text-xs">{isExpense ? 'Dépense' : 'Épargne'}</span></div>
                  <div className="flex items-center gap-4 text-sm"><span className="text-cap-text-muted">Prévu : {fmt(it.planned)}</span><span className="text-white font-medium">Réel : {fmt(it.real)}</span><span className={`font-semibold ${diffCl}`}>{it.diff >= 0 ? '+' : ''}{fmt(it.diff)}</span><Ic className={`w-4 h-4 ${cl}`} /></div>
                </div>); })}</div> : <p className="text-cap-text-muted text-sm">Configurez votre budget dans "Mon Budget" pour voir la comparaison.</p>}
            </div>
            {/* 50/30/20 */}
            <div className="glass-card p-6" data-testid="rule-503020">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Règle 50/30/20</h3>
              <div className="space-y-4">{Object.entries(FAMILY_META).map(([key, meta]) => { const pct = totalRevM > 0 ? (famTotals[key] / totalRevM) * 100 : 0; const inv = key === 'epargne_invest'; const st = inv ? (pct >= meta.target ? 'ok' : pct >= meta.target * 0.7 ? 'warn' : 'bad') : (pct <= meta.target ? 'ok' : pct <= meta.target * 1.2 ? 'warn' : 'bad'); const Ic = st === 'ok' ? CheckCircle : st === 'warn' ? AlertTriangle : XCircle; const cl = st === 'ok' ? 'text-cap-emerald' : st === 'warn' ? 'text-yellow-400' : 'text-cap-error'; return (
                <div key={key} className="p-4 bg-cap-bg rounded-xl" data-testid={`rule-${key}`}>
                  <div className="flex items-center justify-between mb-2"><span className="text-white font-medium text-sm flex items-center gap-2">{meta.emoji} {meta.label} <span className="text-cap-text-muted text-xs">(cible {inv ? '≥' : '≤'} {meta.target}%)</span></span><div className="flex items-center gap-2"><Ic className={`w-4 h-4 ${cl}`} /><span className={`font-display font-bold ${cl}`}>{pct.toFixed(1)}%</span></div></div>
                  <div className="relative h-2.5 bg-cap-bg-elevated rounded-full overflow-hidden"><div className="absolute inset-0 h-full rounded-full opacity-20" style={{ width: `${meta.target}%`, backgroundColor: meta.color }} /><div className="absolute inset-0 h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: meta.color }} /></div>
                  <div className="flex justify-between text-xs text-cap-text-muted mt-1.5"><span>{fmt(famTotals[key])}</span><span>sur {fmt(totalRevM)}</span></div>
                </div>); })}</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Advice */}
              <div className="glass-card p-6" data-testid="advice">{(() => { const a = getAdvice(); return (<div className="p-4 bg-cap-bg rounded-xl"><div className="flex items-start gap-3"><span className="text-2xl">{a.i}</span><div><p className="text-cap-text-secondary text-sm leading-relaxed">{a.t}</p>{profile?.profile_type && <p className="text-cap-text-muted text-xs mt-2">Profil : {profile.profile_type.charAt(0).toUpperCase() + profile.profile_type.slice(1)}</p>}</div></div></div>); })()}</div>
              {/* Donut */}
              <div className="glass-card p-6" data-testid="donut">
                <h3 className="font-display text-base font-semibold text-white mb-3">Répartition par catégorie</h3>
                {catTotals.length > 0 ? (<><div className="h-48"><ResponsiveContainer width="100%" height="100%"><RechartPie><Pie data={catTotals.map(([n, v]) => ({ name: n, value: v }))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">{catTotals.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={v => fmt(v)} /></RechartPie></ResponsiveContainer></div><div className="grid grid-cols-2 gap-1.5 mt-2">{catTotals.map(([n, v], i) => <div key={n} className="flex items-center gap-1.5 text-xs"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} /><span className="text-cap-text-secondary truncate">{n}</span><span className="text-white font-medium ml-auto">{totalExpM > 0 ? ((v / totalExpM) * 100).toFixed(0) : 0}%</span></div>)}</div></>) : <p className="text-cap-text-muted text-sm text-center py-8">Aucune donnée</p>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ═══ TAB 4: MON ÉPARGNE ═══ */}
        <TabsContent value="savings" data-testid="savings-content">
          <div className="flex flex-col">
            {/* Month header */}
            <div className="flex items-center gap-3 mb-6" data-testid="savings-month-header">
              <Wallet className="w-5 h-5 text-cap-emerald" />
              <h2 className="font-display text-lg font-semibold text-white capitalize">
                Épargne — {format(parse(month, 'yyyy-MM', new Date()), 'MMMM yyyy', { locale: fr })}
              </h2>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
              {[
                { type: 'epargne', label: 'Épargne Ce Mois' },
                { type: 'investissement', label: 'Investissement Ce Mois' },
                { type: 'provision', label: 'Provision Ce Mois' }
              ].map(({ type, label }) => (
                <div key={type} className="glass-card p-5">
                  <p className="text-cap-text-muted text-xs mb-1">{label}</p>
                  <p className="font-display text-xl font-bold" style={{ color: savingsBlocks[type].color }}>{fmt(savingsBlocks[type].total)}</p>
                </div>
              ))}
            </div>

            {/* 3 expandable blocks */}
            <div className="flex flex-col" style={{ gap: '16px' }}>
              {['investissement', 'epargne', 'provision'].map(type => {
                const block = savingsBlocks[type];
                const isOpen = expandedBlock === type;
                const subs = Object.entries(block.subs).sort((a, b) => b[1].month - a[1].month);
                const emojis = { investissement: '💙', epargne: '💚', provision: '💜' };
                return (
                  <div key={type} className="rounded-2xl border overflow-hidden transition-colors" style={{ background: 'rgba(26, 29, 39, 0.9)', borderColor: isOpen ? block.color + '40' : 'rgba(255,255,255,0.1)' }} data-testid={`savings-block-${type}`}>
                    <button
                      onClick={() => setExpandedBlock(isOpen ? null : type)}
                      className="w-full flex items-center justify-between p-5 hover:bg-white/[0.03] transition-colors"
                      data-testid={`savings-toggle-${type}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${block.color}15` }}>
                          <Wallet className="w-5 h-5" style={{ color: block.color }} />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-semibold">{emojis[type]} {block.label}</p>
                          <p className="text-cap-text-muted text-xs">{subs.length} sous-catégorie{subs.length !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-display text-xl font-bold" style={{ color: block.color }}>{fmt(block.total)}</p>
                        <ChevronDown className={`w-5 h-5 text-cap-text-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {isOpen && (
                      <div className="border-t border-white/5 p-5 space-y-3">
                        {subs.length > 0 ? subs.map(([name, data]) => (
                          <div key={name} className="flex items-center justify-between p-3 bg-cap-bg rounded-lg flex-wrap gap-2" data-testid={`sav-sub-${name}`}>
                            <div className="flex items-center gap-2.5">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: block.color }} />
                              <span className="text-white text-sm font-medium">{name}</span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="font-semibold" style={{ color: block.color }}>{fmt(data.month)}</span>
                              <span className="text-cap-text-muted">Cumulé : {fmt(data.cum)}</span>
                              {envLinks[name] ? (
                                <span className="flex items-center gap-1 text-cap-blue text-xs">
                                  <Link2 className="w-3 h-3" />
                                  <span className="truncate max-w-[100px]">{objectives.find(o => o.id === envLinks[name])?.name || '—'}</span>
                                  <button onClick={() => unlinkSav(name)} className="text-cap-text-muted hover:text-cap-error"><Unlink className="w-3 h-3" /></button>
                                </span>
                              ) : objectives.length > 0 && (
                                <Select onValueChange={v => linkSavToGoal(name, v)}>
                                  <SelectTrigger className="h-6 w-auto border-none bg-transparent text-cap-blue text-xs p-0 gap-1"><Link2 className="w-3 h-3" /><span>Lier</span></SelectTrigger>
                                  <SelectContent className="bg-cap-bg-elevated border-white/10">{objectives.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                        )) : <p className="text-cap-text-muted text-sm text-center py-4">Aucune dépense ce mois. Ajoutez-en depuis "Mon Mois".</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Historique épargne ── */}
            {savingsHist.some(h => h.total > 0) && (
              <div className="mt-8 space-y-6">

                {/* Courbes cumulées */}
                <div className="glass-card p-6" data-testid="savings-history-chart">
                  <h4 className="text-white text-sm font-semibold mb-4">Épargne cumulée — 12 derniers mois</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={savingsHist}>
                        <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
                        <YAxis stroke="#64748b" fontSize={11} tickFormatter={v => `${v}€`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v, name) => [fmt(v), name]}
                          labelFormatter={(l) => { const h = savingsHist.find(d => d.label === l); return h ? h.fullLabel : l; }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="cumEpargne" stroke="#00e5a0" strokeWidth={2.5} dot={{ r: 3 }} name="Épargne" />
                        <Line type="monotone" dataKey="cumInvest" stroke="#4f8eff" strokeWidth={2.5} dot={{ r: 3 }} name="Investissement" />
                        <Line type="monotone" dataKey="cumProvision" stroke="#7c3aed" strokeWidth={2.5} dot={{ r: 3 }} name="Provisions" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Tableau récapitulatif */}
                <div className="glass-card overflow-hidden" data-testid="savings-history-table">
                  <div className="p-5 border-b border-white/5">
                    <h4 className="text-white text-sm font-semibold">Détail mois par mois</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-cap-text-muted font-medium px-5 py-3">Mois</th>
                          <th className="text-right text-cap-text-muted font-medium px-4 py-3" style={{ color: '#00e5a0' }}>Épargne</th>
                          <th className="text-right text-cap-text-muted font-medium px-4 py-3" style={{ color: '#4f8eff' }}>Investissement</th>
                          <th className="text-right text-cap-text-muted font-medium px-4 py-3" style={{ color: '#7c3aed' }}>Provisions</th>
                          <th className="text-right text-cap-text-muted font-medium px-5 py-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savingsHist.filter(h => h.total > 0 || h.cumTotal > 0).map(h => (
                          <React.Fragment key={h.month}>
                            <tr
                              className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                              onClick={() => setExpandedSavMonth(expandedSavMonth === h.month ? null : h.month)}
                              data-testid={`sav-row-${h.month}`}
                            >
                              <td className="px-5 py-3 text-white font-medium capitalize">{h.fullLabel}</td>
                              <td className="px-4 py-3 text-right" style={{ color: '#00e5a0' }}>{h.epargne > 0 ? fmt(h.epargne) : '—'}</td>
                              <td className="px-4 py-3 text-right" style={{ color: '#4f8eff' }}>{h.invest > 0 ? fmt(h.invest) : '—'}</td>
                              <td className="px-4 py-3 text-right" style={{ color: '#7c3aed' }}>{h.provision > 0 ? fmt(h.provision) : '—'}</td>
                              <td className="px-5 py-3 text-right text-white font-semibold">{h.total > 0 ? fmt(h.total) : '—'}</td>
                            </tr>
                            {expandedSavMonth === h.month && h.detail.length > 0 && (
                              <tr><td colSpan={5} className="px-5 py-3 bg-white/[0.02]">
                                <div className="flex flex-wrap gap-3">
                                  {h.detail.map(d => (
                                    <div key={d.name} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cap-bg">
                                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: DEST_COLORS[d.type] }} />
                                      <span className="text-cap-text-secondary text-xs">{d.name}</span>
                                      <span className="text-white text-xs font-semibold">{fmt(d.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ═══ TAB 5: HISTORIQUE ═══ */}
        <TabsContent value="history" data-testid="history-content">
          <div className="space-y-6">
            {recap && (
              <button onClick={() => setRecapOpen(true)} className="w-full glass-card p-4 flex items-center justify-between hover:border-cap-emerald/30 transition-all group" data-testid="recap-reopen">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-xl ${recap.level === 'green' ? 'bg-emerald-500/15' : recap.level === 'yellow' ? 'bg-amber-500/15' : 'bg-red-500/15'}`}>
                    <CalendarCheck className={`w-5 h-5 ${recap.level === 'green' ? 'text-emerald-400' : recap.level === 'yellow' ? 'text-amber-400' : 'text-red-400'}`} />
                  </div>
                  <div className="text-left">
                    <p className="text-white font-medium text-sm">Bilan de {prevMonthLabel}</p>
                    <p className="text-cap-text-muted text-xs mt-0.5">{recap.message}</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-cap-text-muted group-hover:text-cap-emerald transition-colors" />
              </button>
            )}
            <div className="glass-card p-6" data-testid="hist-bar">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Dépenses totales (6 mois)</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%"><BarChart data={history} barCategoryGap="20%"><XAxis dataKey="label" stroke="#64748b" fontSize={12} /><YAxis stroke="#64748b" fontSize={12} tickFormatter={v => `${v}€`} /><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }} formatter={v => [fmt(v), 'Total']} /><Bar dataKey="total" fill="#ef4444" radius={[5, 5, 0, 0]} barSize={36} /></BarChart></ResponsiveContainer></div>
            </div>
            <div className="glass-card p-6" data-testid="hist-line">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Évolution par famille (6 mois)</h3>
              <div className="h-56"><ResponsiveContainer width="100%" height="100%"><LineChart data={history}><XAxis dataKey="label" stroke="#64748b" fontSize={12} /><YAxis stroke="#64748b" fontSize={12} tickFormatter={v => `${v}€`} /><Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }} formatter={v => fmt(v)} /><Legend /><Line type="monotone" dataKey="obligatoire" stroke="#ef4444" strokeWidth={2} dot={false} name="Besoins" /><Line type="monotone" dataKey="non_essentiel" stroke="#fbbf24" strokeWidth={2} dot={false} name="Envies" /><Line type="monotone" dataKey="epargne_invest" stroke="#00e5a0" strokeWidth={2} dot={false} name="Épargne" /></LineChart></ResponsiveContainer></div>
            </div>
            <div className="glass-card p-6" data-testid="hist-detail">
              <h3 className="font-display text-lg font-semibold text-white mb-4">Détail par mois</h3>
              <div className="space-y-3">
                {history.filter(m => m.total > 0).map(m => (<button key={m.month} onClick={() => { setMonth(m.month); setTab('month'); }} className="w-full p-4 bg-cap-bg rounded-xl flex items-center justify-between hover:bg-white/5 transition-colors text-left"><div><p className="text-white font-medium capitalize">{format(parse(m.month, 'yyyy-MM', new Date()), 'MMMM yyyy', { locale: fr })}</p><p className="text-cap-text-muted text-xs">Revenus: {fmt(m.revenues)} · Épargne: {fmt(m.epargne_invest)}</p></div><div className="flex items-center gap-4"><span className="font-display font-bold text-cap-error">{fmt(m.total)}</span><ChevronRight className="w-4 h-4 text-cap-text-muted" /></div></button>))}
                {history.every(m => m.total === 0) && <p className="text-cap-text-muted text-center py-6">Aucun historique</p>}
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Modal: confirm recurring amount change ── */}
      <Dialog open={recAmountModal.open} onOpenChange={(o) => { if (!o) setRecAmountModal({ open: false, exp: null, oldAmount: 0, newAmount: 0 }); }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-lg w-[92vw]" data-testid="rec-amount-modal">
          <DialogHeader><DialogTitle>Modifier la dépense récurrente</DialogTitle></DialogHeader>
          {recAmountModal.exp && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-cap-text-secondary">
                <span className="text-white font-medium">{recAmountModal.exp.description}</span>
                <span className="mx-2 text-cap-text-muted">·</span>
                <span className="text-cap-text-muted">Actuel : {fmt(recAmountModal.oldAmount)}</span>
              </div>
              <div className="space-y-2">
                <Label className="text-cap-text-secondary text-xs">Nouveau montant (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  autoFocus
                  value={recAmountModal.newAmount}
                  onChange={e => setRecAmountModal({ ...recAmountModal, newAmount: parseFloat(e.target.value) || 0 })}
                  className="bg-cap-bg border-white/10 text-white text-base font-semibold"
                  data-testid="rec-amount-input"
                />
              </div>
              <p className="text-xs text-cap-text-muted">Les mois passés ne seront jamais modifiés — leurs données restent figées.</p>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <Button
                  onClick={() => { applyAmountChange(recAmountModal.exp, recAmountModal.newAmount, 'this-month'); setRecAmountModal({ open: false, exp: null, oldAmount: 0, newAmount: 0 }); }}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/5 h-auto py-3 justify-start text-left whitespace-normal"
                  data-testid="rec-amount-this-month"
                >
                  <div className="w-full">
                    <div className="font-medium">Modifier uniquement ce mois</div>
                    <div className="text-xs text-cap-text-muted mt-0.5 whitespace-normal leading-snug">Le budget global reste inchangé. Override appliqué sur {month}.</div>
                  </div>
                </Button>
                <Button
                  onClick={() => { applyAmountChange(recAmountModal.exp, recAmountModal.newAmount, 'from-now'); setRecAmountModal({ open: false, exp: null, oldAmount: 0, newAmount: 0 }); }}
                  className="bg-cap-emerald text-cap-bg hover:bg-cap-emerald/90 h-auto py-3 justify-start text-left whitespace-normal"
                  data-testid="rec-amount-from-now"
                >
                  <div className="w-full">
                    <div className="font-semibold">Modifier à partir de ce mois</div>
                    <div className="text-xs opacity-80 mt-0.5 whitespace-normal leading-snug">Met à jour Mon Budget. Les mois futurs et l'onglet Analyse utiliseront le nouveau montant.</div>
                  </div>
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modal: recurring expense options (end date) ── */}
      <Dialog open={recOptionsModal.open} onOpenChange={(o) => { if (!o) setRecOptionsModal({ open: false, exp: null, endDate: '' }); }}>
        <DialogContent className="bg-cap-bg-elevated border-white/10 text-white max-w-sm" data-testid="rec-options-modal">
          <DialogHeader><DialogTitle>Options de récurrence</DialogTitle></DialogHeader>
          {recOptionsModal.exp && (
            <div className="space-y-4 mt-2">
              <div className="text-sm text-white font-medium">{recOptionsModal.exp.description}</div>
              <div className="space-y-2">
                <Label className="text-cap-text-secondary text-xs">Date de fin (optionnel)</Label>
                <Input
                  type="date"
                  lang="fr"
                  value={recOptionsModal.endDate || ''}
                  onChange={e => setRecOptionsModal({ ...recOptionsModal, endDate: e.target.value })}
                  className="bg-cap-bg border-white/10 text-white"
                  data-testid="rec-end-date-input"
                />
                <p className="text-xs text-cap-text-muted">Au-delà de cette date, la dépense n'apparaîtra plus sur les mois futurs. Les mois passés restent intacts.</p>
              </div>
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  onClick={() => { applyEndDate(recOptionsModal.exp, ''); setRecOptionsModal({ open: false, exp: null, endDate: '' }); }}
                  className="text-cap-text-muted hover:text-white"
                  data-testid="rec-end-date-clear"
                >
                  Retirer la date
                </Button>
                <Button
                  onClick={() => { applyEndDate(recOptionsModal.exp, recOptionsModal.endDate); setRecOptionsModal({ open: false, exp: null, endDate: '' }); }}
                  className="bg-cap-emerald text-cap-bg hover:bg-cap-emerald/90"
                  data-testid="rec-end-date-save"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinancesPage;
